import { ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { Firestore } from '@angular/fire/firestore';
import { Injectable } from '@angular/core';

import { BrandingService } from '@yoimo/joymo-ui';
import { ChannelWithDocId } from '@yoimo/client-sdk/channels';
import {
  getActivePage,
  getNamedTag,
  NamedTagWithId,
} from '@yoimo/client-sdk/named-tags';
import { HeaderModule, Page, VideoListModule } from '@yoimo/interfaces';
import { Observable, map, of, switchMap, withLatestFrom } from 'rxjs';

import { MetaService, ScopeService } from '@core/services';
import { PageScope } from 'shared-lib/core';
import { mapPageToUIPage } from '@core/utilities/pages';
import { getPageHeader } from '@yoimo/client-sdk/pages';

@Injectable({ providedIn: 'root' })
export class TagPageResolver implements Resolve<Observable<PageScope>> {
  constructor(
    private fs: Firestore,
    private scopeService: ScopeService,
    private metaService: MetaService,
    private brandingService: BrandingService
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<PageScope> {
    const slug = route.paramMap.get('tagSlug') as string;

    return this.scopeService.scope$.pipe(
      switchMap((scope) => getNamedTag(this.fs, scope.scopeId, slug)),
      withLatestFrom(this.scopeService.listenToChannel()),
      map(([namedTag, channel]) => {
        const scope = this.getTagScope(channel, slug, route, namedTag);

        if (namedTag) {
          this.metaService.setTags({
            title: namedTag.displayName || `#${slug}`,
            description: $localize`:@@tagPageMetaTagDescription:Videos & events assiociated to #${namedTag.name}`,
            image: namedTag.cover || '',
          });
        }
        if (scope.activePage?.metaTags) {
          this.metaService.setRawTags(scope.activePage.metaTags);
        }
        if (scope.activePage?.brandingOverride) {
          this.brandingService.setOverride(
            scope.activePage.brandingOverride,
            true
          );
        }

        return scope;
      })
    );
  }

  private getTagScope(
    channel: ChannelWithDocId,
    tag: string,
    route: ActivatedRouteSnapshot,
    namedTag?: NamedTagWithId
  ): PageScope {
    let activePage = namedTag ? getActivePage(namedTag) : null;
    activePage = activePage || this.buildDefaultPage(tag);

    const header: HeaderModule = namedTag
      ? getPageHeader(activePage, { channel, event: undefined, namedTag })
      : {
          text: tag,
          subText: '',
          bannerImages: { '3/1': '', '3/4': '', '16/9': '' },
        };

    const uiActivePage = mapPageToUIPage(
      activePage,
      header,
      namedTag ? 'NAMED_TAG' : 'TAG',
      route.queryParams['tabId']
    );

    return {
      activePage: uiActivePage,
      channel,
      pageDescription: namedTag
        ? {
            title: namedTag.displayName || `#${tag}`,
            description: namedTag?.description || '',
            metaData: namedTag?.metaData,
          }
        : undefined,
      namedTag,
      tag,
      userHasAccess$: of(true),
    };
  }

  private buildDefaultPage(tag: string): Page {
    const videoListModule: VideoListModule = {
      moduleType: 'VIDEOS_LIST',
      listSource: {
        tags: { kind: 'SOME', list: [tag] },
        paged: true,
      },
      layoutType: 'GRID',
      density: 'DENSE',
      size: 12,
      sort: 'DATE_TRIMMED',
      order: 'ASC',
    };
    return {
      name: 'tag_default_page',
      status: 'PUBLISHED',
      active: true,
      modules: {
        all: {
          order: 0,
          condition: null,
          name: $localize`:@@tagPageVideosTabHeading:Videos`,
          modules: [{ ...videoListModule, order: 'DESC' }],
          default: true,
        },
        upcoming: {
          order: 1,
          condition: null,
          name: $localize`: @@tagPageUpcomingTabHeading:Upcoming`,
          modules: [{ ...videoListModule, order: 'ASC' }],
          default: false,
        },
      },
    };
  }
}
